<script lang="ts">
  import { onMount } from "svelte";
  import { appStore } from "../stores/appStore";
  import { getTokenStorage } from "../utils/localStorage";
  import { AppApiClient } from "../api_contracts/app.client";
  import RepositoryHeader from "../components/RepositoryHeader.svelte";
  import RepositoryNav from "../components/RepositoryNav.svelte";
  import FileBrowser from "../components/FileBrowser.svelte";
  import ReadmeViewer from "../components/ReadmeViewer.svelte";
  import EmptyRepositoryHelp from "../components/EmptyRepositoryHelp.svelte";
  import RepositorySidebar from "../components/RepositorySidebar.svelte";
  import FileDetailsView from "../components/FileDetailsView.svelte";
  import FileTreeSidebar from "../components/FileTreeSidebar.svelte";
  import IssuesView from "../components/IssuesView.svelte";
  import PullRequestsView from "../components/PullRequestsView.svelte";
  import SecurityView from "../components/SecurityView.svelte";
  import SettingsView from "../components/SettingsView.svelte";
  import Navbar from "../components/Navbar.svelte";
  import { UserProfile } from "../api_contracts/app";

  import { RepositoryData, FileItem } from "../models/state";
  import { completeRepositoryData } from "../models/seed";

  // State
  let client: AppApiClient | null = $state(null);
  let profile: UserProfile | null = $state(null);
  let token: string = $state("");
  let repoName = $state("");
  // let owner = $state('');
  let activeTab = $state("code");
  let loading = $state(true); // Loading until data is fetched
  let error = $state("");
  let showDetailsView = $state(false);
  let selectedFile: FileItem | null = $state(null);
  let currentPath: string[] = $state([]); // Track current folder path
  let currentFiles: FileItem[] = $state([]); // Files in current directory

  // Repository data - will be loaded from backend
  let repository: RepositoryData | null = $state(null);

  // Branch state
  let branches: string[] = $state([]);
  let currentBranch = $state("main");
  let showBranchDropdown = $state(false);

  // Subscribe to store
  appStore.subscribe((state) => {
    profile = state.profile;
    client = state.client;
    token = state.token;
  });

  const handleTabChange = (tab: string) => {
    activeTab = tab;
  };

  // Refresh repository settings without full page reload
  async function refreshRepositorySettings() {
    if (!client || !token || !profile) return;

    try {
      const result = await client.getRepositoryDetails({
        token,
        ownerId: profile.id,
        repositoryName: repoName,
      });

      if (result.response.successFull && result.response.repository && repository) {
        const repo = result.response.repository;
        // Reassign entire object to trigger Svelte 5 reactivity
        repository = {
          ...repository,
          visibility: repo.visibility as "public" | "private",
          description: repo.description || "",
          name: repo.name,
        };
      }
    } catch (e) {
      console.error("Error refreshing repository settings:", e);
    }
  }

  const handleFileClick = (file: FileItem) => {
    if (file.type === "folder") {
      // Navigate into folder
      currentPath = [...currentPath, file.name];
      updateCurrentFiles();
    } else {
      // Show file details
      selectedFile = file;
      showDetailsView = true;
    }
  };

  const handleBackToFiles = () => {
    showDetailsView = false;
    selectedFile = null;
  };

  const handleNavigateUp = () => {
    if (currentPath.length > 0) {
      currentPath = currentPath.slice(0, -1);
      updateCurrentFiles();
    }
  };

  const navigateToPath = (index: number) => {
    currentPath = currentPath.slice(0, index + 1);
    updateCurrentFiles();
  };

  const handleSidebarNavigate = (path: string[]) => {
    currentPath = path;
    updateCurrentFiles();
    showDetailsView = false;
  };

  const handleSidebarFileClick = (file: FileItem) => {
    selectedFile = file;
    showDetailsView = true;
  };

  // Get files for current path
  const updateCurrentFiles = () => {
    if (!repository || !repository.files) {
      currentFiles = [];
      return;
    }

    let files = repository.files;

    // Navigate through path
    for (const folderName of currentPath) {
      const folder = files.find(
        (f) => f.name === folderName && f.type === "folder",
      );
      if (folder && folder.children) {
        files = folder.children;
      }
    }

    currentFiles = files;
  };

  // Initialize current files
  $effect(() => {
    if (repository && repository.files) {
      updateCurrentFiles();
    }
  });

  async function fetchUserProfile() {
    if (!client || !token) {
      error = "Not authenticated";
      loading = false;
      return false;
    }

    try {
      const result = await client.getUserProfile({ token });

      if (result.response.response.successFull) {
        profile = result.response.profile;
        return true;
      } else {
        error =
          result.response.response.reason || "Failed to fetch user profile.";
        return false;
      }
    } catch (e: any) {
      console.log("here" + e);
      error = e.message || "Error fetching user profile";
      console.error("Error fetching user profile:", e);
      return false;
    }
  }

  async function fetchRepositoryDetails() {
    if (!client || !token) {
      error = "Not authenticated";
      loading = false;
      return;
    }

    try {
      loading = true;
      error = "";

      if (!profile) {
        error = "User profile not loaded,  try logging in again";
        loading = false;
        return;
      }

      const result = await client.getRepositoryDetails({
        token,
        ownerId: profile ? profile.id : "",
        repositoryName: repoName,
      });

      if (result.response.successFull && result.response.repository) {
        const repo = result.response.repository;
        const ownerData = result.response.owner;

        repository = {
          id: repo.id,
          ownerId: repo.ownerId,
          name: repo.name,
          visibility: repo.visibility as "public" | "private",
          watchCount: repo.watchCount,
          starCount: repo.starCount,
          forkCount: repo.forkCount,
          description: repo.description || "",
          website: repo.website || "",
          stars: repo.starCount,
          issueCount: repo.issueCount,
          prCount: repo.prCount,
          branch: repo.defaultBranch,
          owner: {
            id: ownerData.id,
            username: ownerData.userName,
            avatarUrl: ownerData.avatarUrl,
          },
          lastCommit: result.response.lastCommit
            ? {
                message: result.response.lastCommit.message,
                author: result.response.lastCommit.author,
                time: result.response.lastCommit.time,
                sha: result.response.lastCommit.sha,
              }
            : {
                message: "Initial commit",
                author: ownerData.userName,
                time: "just now",
                sha: "a1b2c3d",
              },
          files: [], // Will be filled by fetchRepositoryFiles
          readme: result.response.readme || "",
          languages: result.response.languages
            ? result.response.languages.map((lang) => ({
                name: lang.name,
                percentage: lang.percentage,
                color: lang.color,
              }))
            : [],
        };

        // Fetch files after getting repository details
        await fetchRepositoryFiles();
      } else {
        error = result.response.reason || "Failed to fetch repository details";
      }
    } catch (e: any) {
      error = e.message || "Error fetching repository details";
      console.error("Error fetching repository details:", e);
    } finally {
      loading = false;
    }
  }

  async function fetchRepositoryFiles() {
    if (!client || !token) {
      return;
    }

    if (!profile) {
      return;
    }
    try {
      const result = await client.getRepositoryFiles({
        token,
        ownerId: profile ? profile.id : "",
        repositoryName: repoName,
        branch: currentBranch,
        path: "/",
      });

      if (result.response.successFull && result.response.files) {
        repository.files = result.response.files.map((file) => ({
          name: file.name,
          type: file.type as "file" | "folder",
          message: file.message,
          updatedAt: file.updatedAt,
        }));
      }
    } catch (e: any) {
      console.error("Error fetching repository files:", e);
    }
  }

  async function fetchBranches() {
    if (!client || !token || !profile) {
      return;
    }

    try {
      const result = await client.getBranches({
        token,
        ownerId: profile.id,
        repositoryName: repoName,
      });

      if (result.response.successFull) {
        branches = (result.response.branches || []).map((b: any) => b.name);

        // Set current branch to default if not already set
        if (result.response.defaultBranch && !currentBranch) {
          currentBranch = result.response.defaultBranch;
        }
      }
    } catch (e: any) {
      console.error("Error fetching branches:", e);
    }
  }

  function switchBranch(branchName: string) {
    currentBranch = branchName;
    if (repository) {
      repository.branch = branchName;
    }
    showBranchDropdown = false;
    // Reset path when switching branches
    currentPath = [];
    // Refetch files for new branch
    fetchRepositoryFiles();
  }

  // Helper function to find file by name recursively
  function findFileByName(files: FileItem[], fileName: string): FileItem | null {
    for (const file of files) {
      if (file.name === fileName) {
        return file;
      }
      if (file.type === "folder" && file.children) {
        const found = findFileByName(file.children, fileName);
        if (found) return found;
      }
    }
    return null;
  }

  onMount(() => {
    // Extract username, repo name and optional file name from hash: /users/:username/repositories/:name/files/*
    const hash = window.location.hash;
    const match = hash.match(/\/users\/([^\/]+)\/repositories\/([^\/]+)(?:\/files\/(.+))?/);
    if (match) {
      const username = match[1];
      repoName = match[2];
      const fileName = match[3]; // Optional file name parameter

      // Check if authenticated
      const storedToken = getTokenStorage();
      if (storedToken) {
        if (!profile) {
          // Fetch user profile first to get owner ID
          fetchUserProfile().then((success) => {
            if (success) {
              // Then fetch repository data and branches
              fetchRepositoryDetails().then(() => {
                // After loading files, check if we should open a specific file
                if (fileName && repository && repository.files) {
                  const file = findFileByName(repository.files, fileName);
                  if (file && file.type === "file") {
                    selectedFile = file;
                    showDetailsView = true;
                  }
                }
              });
              fetchBranches();
            } else {
              loading = false;
            }
          });
        } else {
          // Fetch repository data and branches directly
          fetchRepositoryDetails().then(() => {
            // After loading files, check if we should open a specific file
            if (fileName && repository && repository.files) {
              const file = findFileByName(repository.files, fileName);
              if (file && file.type === "file") {
                selectedFile = file;
                showDetailsView = true;
              }
            }
          });
          fetchBranches();
        }
      } else {
        error = "Please login to view this repository";
        loading = false;
      }
    }
  });
</script>

<Navbar />

<div
  class="min-h-screen bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
>
  <div class="container mx-auto px-4 py-8 max-w-7xl">
    {#if loading}
      <div class="flex items-center justify-center min-h-[60vh]">
        <div class="text-center">
          <div
            class="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"
          ></div>
          <p class="mt-4 text-gray-600 dark:text-gray-400">
            Loading repository...
          </p>
        </div>
      </div>
    {:else if error}
      <div class="flex items-center justify-center min-h-[60vh]">
        <div class="text-center">
          <svg
            class="w-16 h-16 mx-auto text-red-500 mb-4"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fill-rule="evenodd"
              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
              clip-rule="evenodd"
            />
          </svg>
          <p class="text-red-600 dark:text-red-400 text-lg font-semibold">
            {error}
          </p>
          <a
            href="#/"
            class="mt-4 inline-block text-blue-600 dark:text-blue-400 hover:underline"
          >
            Go back to home
          </a>
        </div>
      </div>
    {:else if repository}
      <!-- Repository Header -->
      <RepositoryHeader
        repositoryId={repository.id}
        ownerUsername={repository.owner.username}
        name={repository.name}
        visibility={repository.visibility}
        watchCount={repository.watchCount}
        starCount={repository.starCount}
        forkCount={repository.forkCount}
      />

 

      <!-- Repository Navigation -->
      <!-- <RepositoryNav
        {activeTab}
        issueCount={repository.issueCount}
        prCount={repository.prCount}
        onTabChange={handleTabChange}
      /> -->

      <!-- Main Content -->
      <main class="flex gap-0">
        {#if activeTab === "code"}
          <!-- Left Sidebar - File Tree -->
          <FileTreeSidebar 
            files={repository.files}
            {currentPath}
            branch={repository.branch}
            branches={branches}
            currentBranch={currentBranch}
            repoName={repoName}
            onNavigate={handleSidebarNavigate}
            onFileClick={handleSidebarFileClick}
            onBranchSwitch={switchBranch}
          />

          {#if showDetailsView && selectedFile}
            <!-- File/Folder Details View -->
            <div class="flex-1 px-6">
              <FileDetailsView
                file={selectedFile}
                branch={repository.branch}
                {currentPath}
                onBack={handleBackToFiles}
                {repository}
              />
            </div>
          {:else} 
            <!-- Middle Column - Files and README -->
            <div class="flex-1 px-6">
              <!-- File Browser -->
              <FileBrowser
                branch={repository.branch}
                lastCommit={repository.lastCommit}
                files={currentFiles}
                {currentPath}
                onFileClick={handleFileClick}
                onNavigateUp={handleNavigateUp}
                onNavigateToPath={navigateToPath}
                ownerId={repository.ownerId}
                repositoryName={repository.name}
              />

              <!-- Show help text for empty repositories -->
              {#if repository.files.length === 0 && !repository.readme}
                <EmptyRepositoryHelp
                  repositoryName={repository.name}
                  ownerUsername={repository.owner.username}
                />
              {/if}
            </div>
          {/if}
        {:else if activeTab === "issues"}
          <!-- Issues View -->
          <div class="w-full">
            <IssuesView  repositoryName={repoName}
                ownerId={repository.ownerId} />
          </div>
        {:else if activeTab === "pulls"}
          <!-- Pull Requests View -->
          <div class="w-full">
            <PullRequestsView  repositoryName={repoName}
                ownerId={repository.ownerId} />
          </div>
        {:else if activeTab === "security"}
          <!-- Security View -->
          <div class="w-full">
            <SecurityView />
          </div>
        {:else if activeTab === "settings"}
          <!-- Settings View -->
          <div class="w-full">
            {#if repository}
              <SettingsView
                repositoryName={repoName}
                ownerId={repository.ownerId}
                onSettingsUpdate={refreshRepositorySettings}
              />
            {/if}
          </div>
        {/if}
      </main>
    {/if}
  </div>
</div>
